import React, {useMemo, useEffect, useState} from 'react'
import { useTable,
    useGlobalFilter, useFilters,
    useAsyncDebounce,
    usePagination,
    useRowSelect,
    useSortBy} from "react-table";
import {
    shopsData,
    brandsData,
    byEshopTableData,
    byBrandTableData,
    manualPickProductsFromBrandID,
    matchProducts,
    matchProductsBulk,
    deleteProducts,
    removeProducts
} from '../data/api';
import { useRequestQueue } from '../services/requestQueue';
import RequestNotificationPanel from './RequestNotificationPanel';
import {DOTS, useCustomPagination} from './useCustomPagination';
import { CheckCircle2, Trash2, Copy, ListX, Pencil, Archive, Loader2 } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Combobox } from './ui/combobox';
import { TruncatedText } from './ui/truncated-text';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { cn } from '../lib/utils';


export function GlobalFilter({
                                 globalFilter,
                                 setGlobalFilter,
                                 placeholder
                             })
{
    const [value, setValue] = useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 200)

    useEffect(() => {
        setValue(globalFilter || "")
    }, [globalFilter])

    return (
            <Input
                value={value || ""}
                onChange={e => {
                    setValue(e.target.value);
                    onChange(e.target.value);
                }}
                className="w-full min-w-[12rem] sm:min-w-[16rem] md:flex-1"
                type="search"
                placeholder={placeholder}
            />
    )
}


export function ColumnFilter({ column })
{
    if (column && column.colid?.toString() === "bcmatch") {
        const { filterValue, setFilter } = column;
        return (
            <select
                value={filterValue || ""}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
            >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
            </select>
        );
    }
    if (column && (column.Header === "Confidence" || column.Header === "Conf" || column.id === "matchConfidence")) {
        const { filterValue, setFilter } = column;
        return (
            <select
                value={filterValue || ""}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
            >
                <option value="">All</option>
                <option value="over80">Over 80%</option>
                <option value="70-80">70-80%</option>
                <option value="less70">Less than 70%</option>
            </select>
        );
    }
    if (column && column.Header !== "Photo left" && column.Header !== "Actions") {
        const { filterValue, setFilter } = column;

        return (
            <Input
                value={filterValue || ""}
                onChange={(e) => setFilter(e.target.value)}
                className="mt-1 h-8 text-xs"
                type="search"
            />
        )
    }

}


export function PhotoCell({ value }) {
    return (
        <div className="flex items-center">
            <img
                className="h-14 w-14 rounded-md object-cover border bg-muted"
                src={value}
                alt=""
            />
        </div>
    );
}

export function TruncatableCell({ value, href = null, lines = 2 }) {
    return <TruncatedText value={value} href={href} lines={lines} />;
}




// const matchData = (data) => {
//     console.log("- matchData btn -");
//     console.log(data.values);
// }
//
// const deleteData = (data) => {
//     console.log("- deleteData btn -");
//     console.log(data.values);
// }
//
// const removeData = (data) => {
//     console.log("- removeData btn -");
//     console.log(data.values);
// }
//
// const manualPickData = (data) => {
//     console.log("- manualPickData btn -");
//     console.log(data.values);
//
// }



const OverviewTable = ({placeholder}) => {
    const [data, setData] = useState([]);
    const [autoMatchPayloads, setAutoMatchPayloads] = useState([]);
    const [autoMatchLoading, setAutoMatchLoading] = useState(false);
    const [autoMatchPostedCount, setAutoMatchPostedCount] = useState(0);
    const [useSemanticEndpoint, setUseSemanticEndpoint] = useState(true);
    const [clipProductNames, setClipProductNames] = useState(false);
    
    // Request queue for tracking API calls
    const { addRequest, updateRequest, removeRequest, addError } = useRequestQueue();

    const matchData = async (products) => {
        const productRow = products.row.values;
        const requestId = addRequest({
            type: 'match',
            description: `Matching product: ${productRow.product_name || 'Unknown'}`,
            url: productRow.url
        });

        const result = await matchProducts(productRow);
        
        if (result.success) {
            updateRequest(requestId, { status: 'success' });
            const newList = products.data.filter((item) => item.url !== productRow.url);
            setData(newList);
            // Remove success message after 3 seconds
            setTimeout(() => removeRequest(requestId), 3000);
        } else {
            updateRequest(requestId, { status: 'failed' });
            addError({
                type: 'Match Product',
                message: result.error.message || 'Failed to match product',
                url: result.error.url,
                status: result.error.status,
                response: result.error.response
            });
        }
    }

    const deleteData = async (products) => {
        const productRow = products.row.values;
        const requestId = addRequest({
            type: 'delete',
            description: `Deleting product: ${productRow.product_name || 'Unknown'}`,
            url: productRow.url
        });

        const result = await deleteProducts(productRow);
        
        if (result.success) {
            updateRequest(requestId, { status: 'success' });
            const newList = products.data.filter((item) => item.url !== productRow.url);
            setData(newList);
            setTimeout(() => removeRequest(requestId), 3000);
        } else {
            updateRequest(requestId, { status: 'failed' });
            addError({
                type: 'Delete Product',
                message: result.error.message || 'Failed to delete product',
                url: result.error.url,
                status: result.error.status,
                response: result.error.response
            });
        }
    }

    const removeData = async (products) => {
        const productRow = products.row.values;
        const requestId = addRequest({
            type: 'remove',
            description: `Removing product: ${productRow.product_name || 'Unknown'}`,
            url: productRow.url
        });

        const result = await removeProducts(productRow);
        
        if (result.success) {
            updateRequest(requestId, { status: 'success' });
            const newList = products.data.filter((item) => item.url !== productRow.url);
            setData(newList);
            setTimeout(() => removeRequest(requestId), 3000);
        } else {
            updateRequest(requestId, { status: 'failed' });
            addError({
                type: 'Remove Product',
                message: result.error.message || 'Failed to remove product',
                url: result.error.url,
                status: result.error.status,
                response: result.error.response
            });
        }
    }

    const manualPickData = (products) => {
        console.log("- manualPickData btn -");
        console.log(products.values);
        setCurrentProduct(products.values);
        console.log("currentProduct:: ", currentProduct);
        setIsSelectedManualPick(false);
        openModal();
    }


    // Selected Actions Start
    const deleteSelectedData = async () => {
        const requestId = addRequest({
            type: 'delete_bulk',
            description: `Deleting ${selectedFlatRows.length} products`,
        });

        // Process each deletion
        const promises = selectedFlatRows.map(item => deleteProducts(item.values));
        const results = await Promise.all(promises);
        
        const successCount = results.filter(r => r.success).length;
        const failedCount = results.filter(r => !r.success).length;
        
        // Collect successful URLs to remove from table
        const successfulUrls = new Set();
        results.forEach((result, index) => {
            if (result.success) {
                successfulUrls.add(selectedFlatRows[index].values.url);
            }
        });
        
        // Remove only successful products from table
        const newList = data.filter((p) => !successfulUrls.has(p.url));
        setData(newList);
        
        if (failedCount === 0) {
            // All succeeded
            updateRequest(requestId, { status: 'success' });
            setTimeout(() => removeRequest(requestId), 3000);
        } else {
            // Some failed - keep failed products in table
            updateRequest(requestId, { 
                status: 'failed',
                description: `${successCount} succeeded, ${failedCount} failed`
            });
            // Add individual errors for failed products
            results.forEach((result, index) => {
                if (!result.success) {
                    addError({
                        type: 'Delete Product (Bulk)',
                        message: result.error.message || 'Failed to delete product',
                        url: selectedFlatRows[index].values.url,
                        status: result.error.status
                    });
                }
            });
        }
    }

    const removeSelectedData = async () => {
        const requestId = addRequest({
            type: 'remove_bulk',
            description: `Removing ${selectedFlatRows.length} products`,
        });

        const promises = selectedFlatRows.map(item => removeProducts(item.values));
        const results = await Promise.all(promises);
        
        const successCount = results.filter(r => r.success).length;
        const failedCount = results.filter(r => !r.success).length;
        
        // Collect successful URLs to remove from table
        const successfulUrls = new Set();
        results.forEach((result, index) => {
            if (result.success) {
                successfulUrls.add(selectedFlatRows[index].values.url);
            }
        });
        
        // Remove only successful products from table
        const newList = data.filter((p) => !successfulUrls.has(p.url));
        setData(newList);
        
        if (failedCount === 0) {
            // All succeeded
            updateRequest(requestId, { status: 'success' });
            setTimeout(() => removeRequest(requestId), 3000);
        } else {
            // Some failed - keep failed products in table
            updateRequest(requestId, { 
                status: 'failed',
                description: `${successCount} succeeded, ${failedCount} failed`
            });
            results.forEach((result, index) => {
                if (!result.success) {
                    addError({
                        type: 'Remove Product (Bulk)',
                        message: result.error.message || 'Failed to remove product',
                        url: selectedFlatRows[index].values.url,
                        status: result.error.status
                    });
                }
            });
        }
    }

    const manualPickSelectedData = () => {
        setIsSelectedManualPick(true);
        openModal();
    }

    const matchSelectedData = async () => {
        const requestId = addRequest({
            type: 'match_bulk',
            description: `Matching ${selectedFlatRows.length} products`,
        });

        let dataToSend = selectedFlatRows.map(row=>{
            return {
                url: row.values.url,
                idProduct: row.values.rightProductId,
                idEshop: row.values.eshop_id
            }
        });

        const result = await matchProductsBulk(dataToSend);
        
        if (result.success && result.results) {
            // Handle partial success - only remove products that succeeded
            const successfulUrls = new Set();
            const failedUrls = [];
            
            result.results.forEach((item, index) => {
                if (item.result === true || item.queued === true) {
                    successfulUrls.add(dataToSend[index].url);
                } else {
                    failedUrls.push({
                        url: dataToSend[index].url,
                        error: item.error || 'Unknown error'
                    });
                }
            });
            
            // Remove only successful products from table
            const newList = data.filter((p) => !successfulUrls.has(p.url));
            setData(newList);
            
            if (failedUrls.length > 0) {
                // Some failed - mark as partial success
                updateRequest(requestId, { 
                    status: 'failed',
                    description: `${successfulUrls.size} succeeded, ${failedUrls.length} failed`
                });
                // Add errors for failed products
                failedUrls.forEach(failed => {
                    addError({
                        type: 'Match Product (Bulk)',
                        message: failed.error,
                        url: failed.url
                    });
                });
            } else {
                // All succeeded
                updateRequest(requestId, { status: 'success' });
                setTimeout(() => removeRequest(requestId), 3000);
            }
        } else {
            // Complete failure - keep all products in table
            updateRequest(requestId, { status: 'failed' });
            addError({
                type: 'Match Products (Bulk)',
                message: result.error?.message || 'Failed to match products in bulk',
                status: result.error?.status,
                response: result.error?.response
            });
            // Don't remove any products - they all failed
        }
    }
    // Selected Actions End



    const IndeterminateCheckbox = React.forwardRef(
        ({ indeterminate, ...rest }, ref) => {
            const defaultRef = React.useRef()
            const resolvedRef = ref || defaultRef

            React.useEffect(() => {
                resolvedRef.current.indeterminate = indeterminate
            }, [resolvedRef, indeterminate])

            return (
                <>
                    <input type="checkbox" ref={resolvedRef} {...rest} />
                </>
            )
        }
    )

    // let ValuesData = null;
    // console.log("ValuesData", ValuesData);
    // const data = useMemo(() => {
    //     console.log("Memo");
    //     // Perform some expensive computation using the data
    //     return customersData(ValuesData);
    // }, [ValuesData]);
    // import { ShopsByBrand } from '../contexts/ContextVars';
    // const data = ShopsByBrand(1);
    // const data = useMemo(() => customersData(), []);
    // const shops = useMemo(() => shopsData(), []);
    // console.log("eshopsData: ", eshopsData);
    // function MyData(value) {
    //     return useMemo(() => customersData(value), []);
    // }
    // let data = MyData(1555);




    // const columns___ = useMemo(() => [
    //     {
    //         Header: "Customer",
    //         accessor: "customer",
    //         Cell: PhotoCell,
    //         imgAccessor: "imgUrl",
    //         numAccessor: "customerNumber",
    //     },
    //     {
    //         Header: "Deposit",
    //         accessor: "deposit",
    //     },
    //     {
    //         Header: "Date",
    //         accessor: "date",
    //     },
    //     {
    //         Header: "Voucher NO.",
    //         accessor: "voucherNo",
    //     },
    //     {
    //         Header: "Trans.Status",
    //         accessor: "status",
    //         Cell: StatusPill,
    //     },
    //     {
    //         Header: "Actions",
    //         id: 'click-me-button',
    //         accessor: 'id',
    //         // Cell: value=> ButtonsActions(value)
    //         Cell: value=> <>
    //             <Button onClick={() => matchData(value.row)} className="ml-4 px-3 py-1.5 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-green-100" variant="Match"><IoMdDoneAll /></Button>
    //             <Button onClick={() => deleteData(value.row)} className="ml-4 px-3 py-1.5 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-red-100" variant="Delete"><MdDelete /></Button>
    //             <Button onClick={() => removeData(value.row)} className="ml-4 px-3 py-1.5 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-orange-100" variant="Delete"><CgPlayListRemove /></Button>
    //             <Button onClick={() => manualPickData(value.row)} className="ml-4 px-3 py-1.5 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-purple-100" variant="Delete"><CiEdit /></Button>
    //         </>
    //     },
    //
    // ], []);

    const columns = useMemo(() => [
        {
            Header: "Name",
            accessor: "product_name",
            colid: "product_name",
            maxWidth: 220,
            minWidth: 160,
            disableSortBy: true,
            Cell: ({ value, row }) => (
                <TruncatedText
                    value={value}
                    href={row.values.url}
                    lines={clipProductNames ? 2 : 0}
                />
            ),
            Filter : ColumnFilter
        },
        {
            Header: "Barcode",
            accessor: "product_barcode",
            disableSortBy: true,
            Filter : ColumnFilter
        },
        {
            Header: "B-Match",
            accessor: "barcodeMatch",
            colid: "bcmatch",
            minWidth: 90,
            maxWidth: 100,
            disableSortBy: true,
            Filter : ColumnFilter,
            Cell: ({ value }) => (
                <Badge variant={value ? "success" : "danger"}>
                    {value ? "YES" : "No"}
                </Badge>
            )
        },
        {
            Header: "Eshop",
            accessor: "eshop_name",
            colid: "eshop_name",
            maxWidth: 120,
            minWidth: 100,
            disableSortBy: true,
            Filter : ColumnFilter,
            Cell: ({ value }) => <TruncatedText value={value} lines={1} />
        },
        {
            Header: "eshop_id",
            accessor: "eshop_id",
            disableSortBy: true,
            Filter : ColumnFilter
        },
        {
            Header: "Brand",
            minWidth: 110,
            maxWidth: 130,
            accessor: "product_brand_name",
            disableSortBy: true,
            Filter : ColumnFilter,
            Cell: ({ value }) => <TruncatedText value={value} lines={1} />
        },
        {
            Header: "Photo left",
            accessor: "product_photo_left",
            maxWidth: 90,
            minWidth: 90,
            disableSortBy: true,
            Cell: PhotoCell,
            Filter : ColumnFilter
        },
        {
            Header: "Sugg.ID",
            minWidth: 90,
            maxWidth: 100,
            accessor: "rightProductId",
            disableSortBy: true,
            Filter : ColumnFilter
        },
        {
            Header: "Sugg.Product",
            accessor: "rightProductName",
            colid: "rightProductName",
            maxWidth: 220,
            minWidth: 160,
            disableSortBy: true,
            Filter : ColumnFilter,
            Cell: ({ value }) => (
                <TruncatedText value={value} lines={clipProductNames ? 2 : 0} />
            )
        },
        ...(useSemanticEndpoint ? [{
            Header: "Confid",
            accessor: "matchConfidence",
            maxWidth: 90,
            minWidth: 80,
            Filter : ColumnFilter,
            disableSortBy: false,
            filter: (rows, columnIds, filterValue) => {
                if (!filterValue || filterValue === "") {
                    return rows;
                }
                return rows.filter(row => {
                    const confidence = row.values.matchConfidence ?? 0;
                    switch (filterValue) {
                        case "over80":
                            return confidence > 0.8;
                        case "70-80":
                            return confidence >= 0.7 && confidence <= 0.8;
                        case "less70":
                            return confidence < 0.7;
                        default:
                            return true;
                    }
                });
            },
            sortType: (rowA, rowB, columnId) => {
                const a = rowA.values[columnId] ?? 0;
                const b = rowB.values[columnId] ?? 0;
                return a - b;
            },
            Cell: ({ value, row }) => {
                const confidenceValue = value ?? 0;
                const percentage = (confidenceValue * 100).toFixed(1);
                const isBarcodeMatch = row.values.barcodeMatch === true;
                const color = isBarcodeMatch || confidenceValue >= 0.8
                    ? 'text-emerald-600'
                    : confidenceValue >= 0.7
                    ? 'text-amber-600'
                    : 'text-red-600';
                return <div className={cn("font-semibold tabular-nums", color)}>{percentage}%</div>;
            }
        }] : []),
        {
            Header: "Actions",
            id: 'click-me-button',
            accessor: 'id',
            sticky: 'right',
            width: 140,
            minWidth: 120,
            maxWidth: 150,
            disableSortBy: true,
            Cell: (value) => (
                <div className="flex flex-wrap gap-1">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="success" onClick={() => matchData(value)}>
                                <CheckCircle2 />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Match</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="danger" onClick={() => deleteData(value)}>
                                <Trash2 />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="remove" onClick={() => removeData(value)}>
                                <ListX />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Remove</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="pick" onClick={() => manualPickData(value.row)}>
                                <Pencil />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Manual Pick</TooltipContent>
                    </Tooltip>
                </div>
            ),
            Filter : ColumnFilter
        },
        {
            Header: "Url",
            accessor: "url",
            colid: "url",
            maxWidth: 220,
            minWidth: 160,
            disableSortBy: true,
            Filter : ColumnFilter,
            Cell: ({ value }) => {
                const copyToClipboard = async (url) => {
                    try {
                        await navigator.clipboard.writeText(url);
                    } catch (err) {
                        const textArea = document.createElement('textarea');
                        textArea.value = url;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                    }
                };
                if (!value) return <span className="text-muted-foreground">-</span>;
                return (
                    <div className="flex items-center gap-1 min-w-0 max-w-full">
                        <div className="min-w-0 flex-1">
                            <TruncatedText value={value} lines={1} />
                        </div>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size="icon-sm"
                                    variant="outline"
                                    className="shrink-0"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(value);
                                    }}
                                >
                                    <Copy />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Copy URL</TooltipContent>
                        </Tooltip>
                    </div>
                );
            }
        },
    ], [useSemanticEndpoint, clipProductNames]);

    const initialState = { 
        hiddenColumns: ['eshop_id'],
        pageSize: 50  // Set default page size to 50
    };
    
    // Preserve filter state when data changes
    const filterStateRef = React.useRef({ filters: [], globalFilter: undefined });
    const prevDataRef = React.useRef(data);
    
    const { getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        pageCount,
        setPageSize,
        state,
        selectedFlatRows,
        preGlobalFilteredRows,
        setGlobalFilter,

    } = useTable({
            columns,
            data,
            initialState,
        },
        useGlobalFilter,useFilters,
        useSortBy,
        usePagination,
        useRowSelect,
        // useRowSelectColumn,
        hooks => {hooks.visibleColumns.push(columns => [
            // Let's make a column for selection
            {
                // id: 'selection',
                maxWidth: 50,
                width: 50,
                minWidth: 50,
                disableSortBy: true,
                // The header can use the table's getToggleAllPageRowsSelectedProps method
                // to render a checkbox
                Header: ({ getToggleAllPageRowsSelectedProps }) => (
                    <div>
                        <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                    </div>
                ),
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a checkbox
                Cell: ({ row }) => (
                    <div>
                        <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                    </div>
                ),
            },
            ...columns,
        ])}
    );

    const {pageIndex, pageSize, filters, globalFilter} = state;
    
    // Preserve filters when they change
    useEffect(() => {
        if (filters && filters.length > 0) {
            filterStateRef.current.filters = filters;
        }
        if (globalFilter !== undefined) {
            filterStateRef.current.globalFilter = globalFilter;
        }
    }, [filters, globalFilter]);
    
    // Restore filters when data changes (but data reference changed, not just content)
    useEffect(() => {
        const dataChanged = prevDataRef.current !== data;
        if (dataChanged && filterStateRef.current.filters.length > 0) {
            // Restore filters after a brief delay to ensure table is ready
            setTimeout(() => {
                // Restore column filters by setting them on each column
                filterStateRef.current.filters.forEach(filter => {
                    const column = headerGroups
                        .flatMap(hg => hg.headers)
                        .find(col => col.id === filter.id || col.accessor === filter.id);
                    if (column && column.setFilter) {
                        column.setFilter(filter.value);
                    }
                });
                // Restore global filter
                if (filterStateRef.current.globalFilter !== undefined) {
                    setGlobalFilter(filterStateRef.current.globalFilter);
                }
            }, 0);
        }
        prevDataRef.current = data;
    }, [data, setGlobalFilter, headerGroups]);
    
    let paginationRange = useCustomPagination({
        totalPageCount: pageCount,
        siblingCount: 1,
        currentPage: pageIndex
    });

    const setPaginationRange = (v) => {
        const value = parseInt(v)
        const newPageSize = value === 0 ? data.length : value;
        setPageSize(newPageSize);
    }



// ----------------------- Dropdown eshops and brands
    const [shops, setShops] = useState([]);
    const [brands, setBrands] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Loading...");
    const [isLoadingManualPick, setIsLoadingManualPick] = useState(false);
    const [isSelectedManualPick, setIsSelectedManualPick] = useState(false);

    useEffect( () => {
        async function fetchData() {
            try {
                setIsLoading(true);
                setLoadingMessage("Loading Eshops...");
                const eshops = await shopsData();
                const eshopsResults = eshops.results;
                eshopsResults.sort((a, b) => a.name.localeCompare(b.name));
                setShops(eshopsResults);

                setLoadingMessage("Loading Brands...");
                const brands_ = await brandsData();
                const brandsResults = brands_.results;
                brandsResults.sort((a, b) => a.name.localeCompare(b.name));
                setBrands(brandsResults);
                setIsLoading(false);
            } catch (err) {
                console.log(err);
                setIsLoading(false);
            }
        }

        fetchData();
    }, []);
// --------------------------------------------------




    async function SetTableDataAll(value) { // Action from shops drop down
        setIsLoading(true);
        setLoadingMessage("Loading Unmatched...");
        const result = await byEshopTableData(value, (message) => {
            setLoadingMessage(message);
        }, useSemanticEndpoint);
        setData(result.rows || []);
        setAutoMatchPayloads(result.autoMatchPayloads || []);
        setIsLoading(false);


    }
    async function SetTableDataByBrand(value) { // Action from brand drop down
        setIsLoading(true);
        setLoadingMessage("Loading Unmatched...");
        const result = await byBrandTableData(value, (message) => {
            setLoadingMessage(message);
        }, useSemanticEndpoint);
        setData(result.rows || []);
        setAutoMatchPayloads(result.autoMatchPayloads || []);
        setIsLoading(false);
    }

    const postAutoMatches = async () => {
        if (!autoMatchPayloads || autoMatchPayloads.length === 0) return;
        
        const requestId = addRequest({
            type: 'auto_match_bulk',
            description: `Auto-matching ${autoMatchPayloads.length} products`,
        });

        try {
            setAutoMatchLoading(true);
            setAutoMatchPostedCount(0);
            
            const result = await matchProductsBulk(autoMatchPayloads);
            
            if (result.success) {
                updateRequest(requestId, { status: 'success' });
                setAutoMatchPostedCount(autoMatchPayloads.length);
                // remove posted rows from table
                const newList = data.filter((p) => !autoMatchPayloads.some(item => item.url === p.url));
                setData(newList);
                setAutoMatchPayloads([]);
                setTimeout(() => removeRequest(requestId), 3000);
            } else {
                updateRequest(requestId, { status: 'failed' });
                addError({
                    type: 'Auto-Match Products (Bulk)',
                    message: result.error.message || 'Failed to auto-match products',
                    status: result.error.status,
                    response: result.error.response
                });
            }
        } catch (e) {
            updateRequest(requestId, { status: 'failed' });
            addError({
                type: 'Auto-Match Products (Bulk)',
                message: e.message || 'Error posting auto matches',
            });
        } finally {
            setAutoMatchLoading(false);
        }
    }


// ------------------------  MODAL  ------------------------------------
    const [currentProduct, setCurrentProduct] = useState([]);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [productsManualPick, setProductsManualPick] = useState([]);
    const [selectedBrandIDManualPick, setSelectedBrandIDManualPick] = useState('');
    const [selectedEshopId, setSelectedEshopId] = useState('');
    const [selectedBrandId, setSelectedBrandId] = useState('');

    function closeModal() {
        setIsOpen(false);
        setProductsManualPick([]);
        setSelectedBrandIDManualPick('');
        setSelectedProduct('');
    }
    function openModal() {
        setIsOpen(true);
    }
    async function saveProduct() {
        if (!selectedProduct) return;

        if (isSelectedManualPick) {
            const requestId = addRequest({
                type: 'manual_pick_bulk',
                description: `Manual pick for ${selectedFlatRows.length} products`,
            });

            const promises = selectedFlatRows.map(item => {
                item.values.rightProductId = parseInt(selectedProduct);
                return matchProducts(item.values);
            });
            
            const results = await Promise.all(promises);
            const failedCount = results.filter(r => !r.success).length;
            
            if (failedCount === 0) {
                updateRequest(requestId, { status: 'success' });
                const newList = data.filter((p) =>
                    !selectedFlatRows.some(item => item.values.url === p.url)
                );
                setData(newList);
                setTimeout(() => removeRequest(requestId), 3000);
            } else {
                updateRequest(requestId, { status: 'failed' });
                results.forEach((result, index) => {
                    if (!result.success) {
                        addError({
                            type: 'Manual Pick (Bulk)',
                            message: result.error.message || 'Failed to match product',
                            url: selectedFlatRows[index].values.url,
                            status: result.error.status
                        });
                    }
                });
            }
        } else {
            const requestId = addRequest({
                type: 'manual_pick',
                description: `Manual pick: ${currentProduct.product_name || 'Unknown'}`,
                url: currentProduct.url
            });

            currentProduct.rightProductId = parseInt(selectedProduct);
            const result = await matchProducts(currentProduct);
            
            if (result.success) {
                updateRequest(requestId, { status: 'success' });
                const newList = data.filter((p) => p.url !== currentProduct.url);
                setData(newList);
                setTimeout(() => removeRequest(requestId), 3000);
            } else {
                updateRequest(requestId, { status: 'failed' });
                addError({
                    type: 'Manual Pick',
                    message: result.error.message || 'Failed to match product',
                    url: result.error.url,
                    status: result.error.status,
                    response: result.error.response
                });
            }
        }
        setIsSelectedManualPick(false);
        closeModal();
    }
    async function searchModalProduct(BrandID) {
        setIsLoadingManualPick(true);
        const prods = await manualPickProductsFromBrandID(BrandID);
        const prodsResults = prods.results.sort((a, b) =>
            (a.name ?? `Empty`).localeCompare(b.name ?? `Empty`, ['el', 'en'], { sensitivity: 'base' })
        );
        setProductsManualPick(prodsResults);
        setIsLoadingManualPick(false);
    }

    const shopOptions = shops.map((s) => ({ value: s.idEshop, label: s.name }));
    const brandOptions = brands.map((b) => ({ value: b.idBrand, label: b.name }));
    const manualBrandOptions = brands.map((b) => ({ value: b.idBrand, label: b.name }));
    const manualProductOptions = productsManualPick.map((p) => ({
        value: p.idProduct,
        label: p.name || `Product ${p.idProduct}`,
    }));

// ---------------------------------------------------------------------

    return (
        <TooltipProvider delayDuration={200}>
        <div className="space-y-4 w-full max-w-[100vw] px-1 sm:px-2">
            <RequestNotificationPanel />

            <Dialog open={modalIsOpen} onOpenChange={(open) => !open && closeModal()}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Manual Pick</DialogTitle>
                        <DialogDescription className="break-words">
                            Match to product:{' '}
                            <span className="font-medium text-foreground">
                                {currentProduct?.product_name || (isSelectedManualPick ? `${selectedFlatRows.length} selected` : '—')}
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Brand</Label>
                            <Combobox
                                options={manualBrandOptions}
                                value={selectedBrandIDManualPick}
                                onValueChange={(v) => {
                                    setSelectedBrandIDManualPick(v);
                                    setSelectedProduct('');
                                    searchModalProduct(v);
                                }}
                                placeholder="Select brand…"
                                searchPlaceholder="Search brands…"
                                className="w-full"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Product</Label>
                            {isLoadingManualPick ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Loading products…
                                </div>
                            ) : (
                                <Combobox
                                    options={manualProductOptions}
                                    value={selectedProduct}
                                    onValueChange={setSelectedProduct}
                                    placeholder="Select product…"
                                    searchPlaceholder="Search products…"
                                    className="w-full"
                                    emptyText="No products found."
                                />
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={closeModal}>Cancel</Button>
                        <Button onClick={saveProduct} disabled={!selectedProduct}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Header / controls */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 min-h-[1.5rem]">
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{loadingMessage}</span>
                        </div>
                    ) : (
                        <span className="text-sm text-muted-foreground">
                            {data.length} product{data.length === 1 ? '' : 's'}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <Switch
                            id="semantic-toggle"
                            checked={useSemanticEndpoint}
                            onCheckedChange={(checked) => {
                                setUseSemanticEndpoint(checked);
                                setData([]);
                                setSelectedEshopId('');
                                setSelectedBrandId('');
                            }}
                        />
                        <Label htmlFor="semantic-toggle" className="cursor-pointer">
                            {useSemanticEndpoint ? 'Semantic Matching' : 'Classic Matching'}
                        </Label>
                    </div>
                    <div className="flex items-center gap-3">
                        <Switch
                            id="clip-names-toggle"
                            checked={clipProductNames}
                            onCheckedChange={setClipProductNames}
                        />
                        <Label htmlFor="clip-names-toggle" className="cursor-pointer">
                            Clip names
                        </Label>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
                <Combobox
                    options={shopOptions}
                    value={selectedEshopId}
                    onValueChange={(v) => {
                        setSelectedEshopId(v);
                        setSelectedBrandId('');
                        setData([]);
                        SetTableDataAll(v);
                    }}
                    placeholder="Select eshop…"
                    searchPlaceholder="Search eshops…"
                    className="w-full lg:w-56"
                    disabled={isLoading && shops.length === 0}
                />
                <Combobox
                    options={brandOptions}
                    value={selectedBrandId}
                    onValueChange={(v) => {
                        setSelectedBrandId(v);
                        setSelectedEshopId('');
                        setData([]);
                        SetTableDataByBrand(v);
                    }}
                    placeholder="Select brand…"
                    searchPlaceholder="Search brands…"
                    className="w-full lg:w-56"
                    disabled={isLoading && brands.length === 0}
                />
                <GlobalFilter
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    placeholder={placeholder}
                />
                <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="success" onClick={matchSelectedData} disabled={!selectedFlatRows.length}>
                                <CheckCircle2 />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Match selected</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                size="icon"
                                variant="info"
                                onClick={postAutoMatches}
                                disabled={autoMatchLoading || autoMatchPayloads.length === 0}
                            >
                                {autoMatchLoading ? <Loader2 className="animate-spin" /> : <Archive />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Post auto-matches ({autoMatchPayloads.length})</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="danger" onClick={deleteSelectedData} disabled={!selectedFlatRows.length}>
                                <Trash2 />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete selected</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="remove" onClick={removeSelectedData} disabled={!selectedFlatRows.length}>
                                <ListX />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Remove selected</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="pick" onClick={manualPickSelectedData} disabled={!selectedFlatRows.length}>
                                <Pencil />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Manual pick selected</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table {...getTableProps()} className="w-full min-w-[900px] text-sm">
                        <thead className="bg-muted/50 border-b">
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    const isConfidenceColumn = column.id === 'matchConfidence';
                                    const headerProps = column.getHeaderProps();
                                    return (
                                        <th {...headerProps}
                                            className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground align-top"
                                            style={{
                                                maxWidth: column.maxWidth || (column.width ? column.width : 'none'),
                                                width: column.width || (column.maxWidth ? column.maxWidth : 'auto'),
                                                minWidth: column.minWidth || (column.width ? column.width : 'auto'),
                                                position: column.sticky === 'right' ? 'sticky' : 'relative',
                                                right: column.sticky === 'right' ? 0 : 'auto',
                                                backgroundColor: column.sticky === 'right' ? 'hsl(var(--muted))' : 'inherit',
                                                zIndex: column.sticky === 'right' ? 10 : 1,
                                                boxShadow: column.sticky === 'right' ? '-2px 0 4px rgba(0,0,0,0.06)' : 'none'
                                            }}
                                        >
                                            <div className="flex items-center gap-1">
                                                {column.render("Header")}
                                                {column.canSort && isConfidenceColumn && (() => {
                                                    const sortProps = column.getSortByToggleProps();
                                                    const originalOnClick = sortProps.onClick;
                                                    return (
                                                        <span
                                                            {...sortProps}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (originalOnClick) originalOnClick(e);
                                                            }}
                                                            className="cursor-pointer select-none hover:text-foreground"
                                                        >
                                                            {column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? ' ↓'
                                                                    : ' ↑'
                                                                : ' ⇅'}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                                            {column.id === 'selection' && column.render('Summary')}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()} className="divide-y">
                            {page.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="hover:bg-muted/30">
                                        {row.cells.map((cell) => {
                                            const isNameColumn = cell.column?.colid?.toString() === 'product_name';
                                            const isEshopNameColumn = cell.column?.colid?.toString() === 'eshop_name';
                                            const isSuggProductColumn = cell.column?.colid?.toString() === 'rightProductName';
                                            const isUrlColumn = cell.column?.colid?.toString() === 'url';
                                            const shouldWrap = isNameColumn || isSuggProductColumn || isUrlColumn || isEshopNameColumn;
                                            const cellProps = cell.getCellProps();
                                            const cellStyle = cellProps.style || {};
                                            return <td {...cellProps}
                                                className="px-3 py-2 align-middle"
                                                style={{
                                                    ...cellStyle,
                                                    maxWidth: cell.column.maxWidth || (cell.column.width ? cell.column.width : 'none'),
                                                    width: cell.column.width || (cell.column.maxWidth ? cell.column.maxWidth : 'auto'),
                                                    minWidth: cell.column.minWidth || (cell.column.width ? cell.column.width : 'auto'),
                                                    whiteSpace: shouldWrap ? 'normal' : (cellStyle.whiteSpace || 'nowrap'),
                                                    overflow: 'hidden',
                                                    position: cell.column.sticky === 'right' ? 'sticky' : 'relative',
                                                    right: cell.column.sticky === 'right' ? 0 : 'auto',
                                                    backgroundColor: cell.column.sticky === 'right' ? 'hsl(var(--background))' : 'inherit',
                                                    zIndex: cell.column.sticky === 'right' ? 10 : 1,
                                                    boxShadow: cell.column.sticky === 'right' ? '-2px 0 4px rgba(0,0,0,0.06)' : 'none'
                                                }}
                                            >{cell.render("Cell")}</td>
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-4">
                <Button variant="outline" onClick={() => previousPage()} disabled={!canPreviousPage}>
                    Previous
                </Button>
                <div className="flex flex-wrap items-center justify-center gap-1">
                    {paginationRange?.map((pageNumber, index) => {
                        if (pageNumber === DOTS) {
                            return (
                                <Button key={index} variant="ghost" size="sm" disabled>
                                    …
                                </Button>
                            );
                        }
                        const active = (pageNumber - 1) === pageIndex;
                        return (
                            <Button
                                key={index}
                                variant={active ? "default" : "outline"}
                                size="sm"
                                onClick={() => gotoPage(pageNumber - 1)}
                            >
                                {pageNumber}
                            </Button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-2 justify-end">
                    <select
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={pageSize === 9999999999 ? 9999999999 : pageSize}
                        onChange={e => setPaginationRange(e.target.value)}
                    >
                        <option value={9999999999}>All</option>
                        <option value={3}>03</option>
                        <option value={7}>07</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <Button variant="outline" onClick={() => nextPage()} disabled={!canNextPage}>
                        Next
                    </Button>
                </div>
            </div>
        </div>
        </TooltipProvider>
    )
}

export default OverviewTable

